/**
 * Placement de la main cartoon qui pince le menton de l'ami. Calculs en pixels
 * de l'écran (après projection), pour que la contrainte « jamais sur la bouche »
 * soit vérifiée sur ce qui est réellement affiché.
 */
import { FaceLandmarker } from '@mediapipe/tasks-vision';
import type { Point3 } from '../detection/types';
import type { Projector } from './projection';

export interface Vec {
  readonly x: number;
  readonly y: number;
}

const FOREHEAD = 10;
const CHIN = 152;
const CHEEK_LEFT = 234;
const CHEEK_RIGHT = 454;
const LIPS = [...new Set(FaceLandmarker.FACE_LANDMARKS_LIPS.flatMap((c) => [c.start, c.end]))];
const MIN_POINTS = 468;

export interface ChinAnchor {
  readonly chin: Vec;
  /** Axe « vers le bas » du visage (front → menton), unitaire. */
  readonly down: Vec;
  /** Inclinaison du visage (rad) : 0 = droit. */
  readonly angle: number;
  /** Distance joue à joue (px) : l'échelle du visage. */
  readonly faceWidth: number;
  /** Espace (px) entre le point le plus bas des lèvres et le menton, le long de `down`. */
  readonly mouthClearance: number;
}

/** Géométrie de l'image de la main (fractions de sa taille). */
export interface HandGeometry {
  /** Largeur de la main / largeur du visage. */
  readonly widthRatio: number;
  /** Hauteur / largeur de l'image. */
  readonly aspect: number;
  /** Point de pincement dans l'image (0..1). */
  readonly pinch: Vec;
  /** Marge minimale sous la bouche, en fraction de la largeur du visage. */
  readonly marginRatio: number;
}

export const GLOVE: HandGeometry = {
  widthRatio: 0.75,
  aspect: 1.1,
  pinch: { x: 0.5, y: 0.15 },
  marginRatio: 0.03,
};

export interface HandPlacement {
  /** Point de pincement à l'écran (px). */
  readonly x: number;
  readonly y: number;
  readonly angle: number;
  readonly width: number;
  readonly height: number;
  /** La main a été descendue pour ne pas couvrir la bouche. */
  readonly clamped: boolean;
}

const dot = (a: Vec, b: Vec): number => a.x * b.x + a.y * b.y;

export function chinAnchor(points: readonly Point3[], project: Projector): ChinAnchor | null {
  if (points.length < MIN_POINTS) return null;
  const px = (i: number): Vec => project(points[i] as Point3);
  const top = px(FOREHEAD);
  const chin = px(CHIN);
  const len = Math.hypot(chin.x - top.x, chin.y - top.y);
  if (!(len > 0)) return null;
  const down = { x: (chin.x - top.x) / len, y: (chin.y - top.y) / len };
  const left = px(CHEEK_LEFT);
  const right = px(CHEEK_RIGHT);
  const lowestLip = Math.max(...LIPS.map((i) => dot(px(i), down)));
  return {
    chin,
    down,
    angle: Math.atan2(-down.x, down.y),
    faceWidth: Math.hypot(right.x - left.x, right.y - left.y),
    mouthClearance: dot(chin, down) - lowestLip,
  };
}

/**
 * Les doigts pincent le menton ; si la bouche en est trop proche (fou rire,
 * bouche grande ouverte), la main descend : elle ne couvre JAMAIS la bouche.
 */
export function placeHand(anchor: ChinAnchor, geom: HandGeometry = GLOVE): HandPlacement {
  const width = anchor.faceWidth * geom.widthRatio;
  const height = width * geom.aspect;
  const needed = geom.pinch.y * height + geom.marginRatio * anchor.faceWidth;
  const shift = Math.max(0, needed - anchor.mouthClearance);
  return {
    x: anchor.chin.x + anchor.down.x * shift,
    y: anchor.chin.y + anchor.down.y * shift,
    angle: anchor.angle,
    width,
    height,
    clamped: shift > 0,
  };
}

/** Coins de l'image de la main à l'écran (haut-gauche, haut-droit, bas-droit, bas-gauche). */
export function handCorners(h: HandPlacement, geom: HandGeometry = GLOVE): Vec[] {
  const cos = Math.cos(h.angle);
  const sin = Math.sin(h.angle);
  return [
    [0, 0],
    [1, 0],
    [1, 1],
    [0, 1],
  ].map(([u = 0, v = 0]) => {
    const lx = (u - geom.pinch.x) * h.width;
    const ly = (v - geom.pinch.y) * h.height;
    return { x: h.x + lx * cos - ly * sin, y: h.y + lx * sin + ly * cos };
  });
}
